import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { InventoryItem, Plant, GardenObject } from "../types";
import ItemSelector, { ItemSelectorRef } from "./ItemSelector";
import "./InventoryEditForm.css";

interface InventoryEditFormProps {
  item: InventoryItem | null;
  _onSave: (item: InventoryItem) => void;
  company_id: string;
}

export interface InventoryEditFormRef {
  getFormData: () => InventoryItem;
}

const InventoryEditForm = forwardRef<
  InventoryEditFormRef,
  InventoryEditFormProps
>(({ item, _onSave, company_id }, ref) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<InventoryItem>({
    item_id: "",
    type: "",
    quantity: "",
    selling_price: "",
    unit_cost: "",
    supplier: "",
    restock_threshold: "",
    low_stock_threshold: "",
    location_in_warehouse: "",
    status: "in_stock",
  });

  // State for selected item from ItemSelector
  const [selectedItem, setSelectedItem] = useState<Plant | GardenObject | null>(
    null
  );
  const [selectedItemType, setSelectedItemType] = useState<"plant" | "object">(
    "plant"
  );

  // Ref for ItemSelector to access its data
  const itemSelectorRef = useRef<ItemSelectorRef>(null);

  // Expose getFormData method to parent component
  useImperativeHandle(ref, () => ({
    getFormData: () => formData,
  }));

  // These would normally be fetched from the COMPANY_SETTINGS in the DynamoDB table
  const inventoryStatus = ["active", "discontinued"];

  // Handle form data population/reset based on item prop
  useEffect(() => {
    if (!item) {
      // Reset form for new items
      setFormData({
        item_id: "",
        type: "",
        quantity: "",
        selling_price: "",
        unit_cost: "",
        supplier: "",
        restock_threshold: "",
        low_stock_threshold: "",
        location_in_warehouse: "",
        status: "in_stock",
      });
      setSelectedItem(null);
      setSelectedItemType("plant");
    } else {
      // Populate form for editing existing items
      setFormData({
        ...item,
        quantity:
          item.quantity === 0 || item.quantity === "0"
            ? ""
            : String(item.quantity || ""),
        selling_price:
          item.selling_price === 0 || item.selling_price === "0"
            ? ""
            : String(item.selling_price || ""),
        unit_cost:
          item.unit_cost === 0 || item.unit_cost === "0"
            ? ""
            : String(item.unit_cost || ""),
        restock_threshold:
          item.restock_threshold === 0 || item.restock_threshold === "0"
            ? "10"
            : String(item.restock_threshold || ""),
        low_stock_threshold:
          item.low_stock_threshold === 0 || item.low_stock_threshold === "0"
            ? "10"
            : String(item.low_stock_threshold || ""),
      });

      // Set selected item type based on existing item
      if (item.type) {
        setSelectedItemType(item.type as "plant" | "object");
      }

      // Set selected item from enriched data when editing existing items
      if (item.type === "plant" && item.plant_data) {
        setSelectedItem(item.plant_data);
      } else if (item.type === "object" && item.object_data) {
        console.log("item.object_data : ", item.object_data);
        setSelectedItem(item.object_data);
      } else {
        // Fallback: clear selected item if no enriched data is available
        setSelectedItem(null);
      }
    }
  }, [item]);

  // Handler for when an item is selected from ItemSelector
  const handleItemSelected = (
    item: Plant | GardenObject | null,
    type: "plant" | "object"
  ) => {
    setSelectedItem(item);
    setSelectedItemType(type);

    if (item) {
      // Populate form fields based on selected item

      setFormData((prev) => ({
        ...prev,
        item_id:
          type === "plant"
            ? (item as Plant).plant_id || ""
            : (item as GardenObject).object_id || "",
        type: type,
      }));
    } else {
      // Clear form fields when no item is selected
      setFormData((prev) => ({
        ...prev,
        name: "",
        type: "",
      }));
    }
  };

  // Generate random dummy data for testing
  const generateDummyData = () => {
    const suppliers = [
      "GreenThumb Supplies",
      "Garden Depot",
      "Plant Paradise",
      "Nature's Best",
      "Bloom & Grow",
      "Evergreen Nursery",
      "Sunshine Gardens",
      "Terra Firma",
    ];

    const locations = [
      "Aisle 1",
      "Aisle 2",
      "Aisle 3",
      "Section A",
      "Section B",
      "Warehouse North",
      "Warehouse South",
      "Storage Room 1",
      "Storage Room 2",
    ];

    const quantity = Math.floor(Math.random() * 100) + 1; // 1-100
    const unitCost = Math.floor(Math.random() * 50) + 5; // $5-55
    const profitMargin = 1.3 + Math.random() * 0.7; // 30-100% profit margin
    const sellingPrice = Math.round(unitCost * profitMargin * 100) / 100;
    const restockThreshold = Math.floor(Math.random() * 20) + 5; // 5-25
    const lowStockThreshold = Math.floor(Math.random() * 10) + 2; // 2-12

    // Randomly select an item from ItemSelector's data
    let selectedItemData: {
      item: Plant | GardenObject;
      type: "plant" | "object";
    } | null = null;
    if (itemSelectorRef.current) {
      selectedItemData = itemSelectorRef.current.getRandomItem();
    }

    if (selectedItemData) {
      // Update selected item and type
      setSelectedItem(selectedItemData.item);
      setSelectedItemType(selectedItemData.type);
    }

    // Generate dummy data
    setFormData((prev) => ({
      ...prev,
      item_id: selectedItemData
        ? selectedItemData.type === "plant"
          ? (selectedItemData.item as Plant).plant_id || ""
          : (selectedItemData.item as GardenObject).object_id || ""
        : "",
      type: selectedItemData ? selectedItemData.type : prev.type,
      quantity: quantity.toString(),
      selling_price: sellingPrice.toString(),
      unit_cost: unitCost.toString(),
      supplier: suppliers[Math.floor(Math.random() * suppliers.length)],
      restock_threshold: restockThreshold.toString(),
      low_stock_threshold: lowStockThreshold.toString(),
      location_in_warehouse:
        locations[Math.floor(Math.random() * locations.length)],
      status: "in_stock",
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Handle numeric values
    if (
      name === "quantity" ||
      name === "selling_price" ||
      name === "unit_cost" ||
      name === "restock_threshold" ||
      name === "low_stock_threshold"
    ) {
      // Allow empty values for better UX - keep as string for form display
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  return (
    <div className="inventory-edit-form">
      <div className="d-flex justify-content-between align-items-center mb-3">
        {import.meta.env.VITE_DEV_AUTH_BYPASS && (
          <>
            <h5 className="mb-0">Inventory Item Details</h5>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={generateDummyData}
              title="Fill form with random test data"
            >
              <i className="bi bi-magic me-1"></i>
              Generate Dummy Data
            </button>
          </>
        )}
      </div>
      {/* Item Selection */}
      <div className={item ? "item-selector-disabled" : ""}>
        <ItemSelector
          ref={itemSelectorRef}
          onItemSelected={handleItemSelected}
          selectedItem={selectedItem}
          selectedType={selectedItemType}
          company_id={company_id}
          disabled={!!item}
        />
      </div>

      <div className="form-group">
        <label htmlFor="quantity">{t("forms.labels.quantity")}</label>
        <input
          type="number"
          className="form-control"
          id="quantity"
          name="quantity"
          min="0"
          value={String(formData.quantity || "")}
          onChange={handleInputChange}
          placeholder="0"
          required
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
        />
      </div>

      <div className="form-group">
        <label htmlFor="unit_cost">Unit Cost ($)</label>
        <input
          type="number"
          className="form-control"
          id="unit_cost"
          name="unit_cost"
          min="0"
          step="0.01"
          value={String(formData.unit_cost || "")}
          onChange={handleInputChange}
          placeholder="0.00"
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
        />
      </div>

      <div className="form-group">
        <label htmlFor="selling_price">Selling Price ($)</label>
        <input
          type="number"
          className="form-control"
          id="selling_price"
          name="selling_price"
          min="0"
          step="0.01"
          value={String(formData.selling_price || "")}
          onChange={handleInputChange}
          placeholder="0.00"
          required
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
        />
      </div>

      <div className="form-group">
        <label htmlFor="supplier">Supplier</label>
        <input
          type="text"
          className="form-control"
          id="supplier"
          name="supplier"
          value={String(formData.supplier || "")}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group">
        <label htmlFor="restock_threshold">Restock Threshold</label>
        <input
          type="number"
          className="form-control"
          id="restock_threshold"
          name="restock_threshold"
          min="0"
          value={String(formData.restock_threshold || "")}
          onChange={handleInputChange}
          placeholder="0"
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
        />
      </div>

      <div className="form-group">
        <label htmlFor="low_stock_threshold">Low Stock Threshold</label>
        <input
          type="number"
          className="form-control"
          id="low_stock_threshold"
          name="low_stock_threshold"
          min="0"
          value={String(formData.low_stock_threshold || "")}
          onChange={handleInputChange}
          placeholder="0"
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
        />
      </div>

      <div className="form-group">
        <label htmlFor="location_in_warehouse">Location in Warehouse</label>
        <input
          type="text"
          className="form-control"
          id="location_in_warehouse"
          name="location_in_warehouse"
          value={String(formData.location_in_warehouse || "")}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group">
        <label htmlFor="status">{t("forms.labels.status")}</label>
        <select
          className="form-select"
          id="status"
          name="status"
          value={String(formData.status || "in_stock")}
          onChange={handleInputChange}
        >
          {inventoryStatus.map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, " ")}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
});

InventoryEditForm.displayName = "InventoryEditForm";

export default InventoryEditForm;
